import { Component, Input, ViewChild, OnInit, TemplateRef, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { DatatableComponent, ColumnMode, SelectionType } from "@swimlane/ngx-datatable";
import { Column } from "@universal-ui";
import { parse, parseISO, format, isValid, differenceInDays } from "date-fns";
import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { isObservable, Observable } from "rxjs";

import datatableMessages from "@assets/datatables/table-messages.json";

import { NumberPipe } from "@shared/Pipes/number.pipe";
import { CacheService } from "@shared/Services/cache.service";
import { ExcelService } from "@shared/Services/excel.service";
import { ObjectPropertiesService } from "@shared/Services/objectProperties.service";

import { UnitSharedService } from "@Unit/shared/services/unitshared.service";

import { RowClickEvent, SortEvent } from "../../../Fiduciaria/RequestCreation/Types/Interfaces/event-sort.interface";

import { FilterTableModal } from "./Modal/modal.component";

@Component({
  selector: "filter-table",
  templateUrl: "./filterTable.component.html",
  styleUrls: ["./filterTable.component.scss"],
})
export class FilterTableComponent<T> implements OnInit, OnChanges {
  public offset: number = 0;
  public maxPage: number = 0;

  @Input() public currentPage: number = 1;
  @Output() public currentPageChange: EventEmitter<number> = new EventEmitter<number>();

  @Input() public pageSize: number = 10;
  @Output() public pageSizeChange: EventEmitter<number> = new EventEmitter<number>();

  public allSelected: boolean = true;
  public isUnitPortalStyle: boolean = false;

  public tableId: string;
  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;

  // TABLE PARAMETERS
  @Input() public columnMode?: string;
  @Input() public selectionType?: string;

  // FILTER
  public filterValue: string = "";

  // ALL COLUMNS
  @Input() public columns: Column[] = [];
  public displayColumns: Column[] = [];

  // COLUMNS TO FILTER ON
  @Input() public searchCols: string[] = [];

  // INPUT DATA
  // COULD BE AN OBSERVABLE, OR AN ARRAY OF OBJECTS
  @Input() public data: T[] = [];
  public displayData: T[] = [];
  public filteredData: T[] = [];
  public originalData: T[] = [];

  @Input() public loading: boolean = false;

  // ITEM COUNT
  public itemCount: number = 0;

  // SELECTED DATA
  @Input() public selectableRow: boolean = true;
  @Input() public selectedData: T[] = [];

  // EXCEL
  @Input() public excelName?: string;
  @Input() public customExport?: boolean;
  @Output() public excelExported: EventEmitter<T[]> = new EventEmitter<T[]>();

  // ROW CLICK
  @Input() public clickableRow: boolean = false;
  @Output() public rowClicked: EventEmitter<T> = new EventEmitter<T>();
  @Output() public selectRow: EventEmitter<T[]> = new EventEmitter<T[]>();

  // RESPONSIVE MODAL
  public filterTableModal?: BsModalRef;

  @ViewChild(DatatableComponent, { static: true }) private table?: DatatableComponent;
  public datatableMessages = { ...datatableMessages };

  // TEMPLATES
  @ViewChild("yesNoTemplate", { static: true }) public yesNoTemplate?: TemplateRef<boolean>;

  public shownPageNumber: number = 3;
  private OBJECT_DELIMITER: string = ".";
  public pageCountArray: { value: number }[] = [];
  public pageDisplayLimitArray: { value: number }[] = [{ value: 5 }, { value: 10 }, { value: 20 }, { value: 30 }, { value: 50 }];

  constructor(
    private excelService: ExcelService,
    private bsModalService: BsModalService,
    private cacheService: CacheService,
    private objectPropertiesService: ObjectPropertiesService,
    private unitSharedService: UnitSharedService
  ) {
    this.singleSelectCheck = this.singleSelectCheck.bind(this);
    this.unitSharedService.isUnitPortalStyle$.subscribe((isUnit) => {
      this.isUnitPortalStyle = isUnit;
    });
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  public ngOnInit(): void {
    this.setTableId();
    this.loadTableCache();
    this.setColumnWidth();
    setTimeout(() => {
      if (!this.allSelected) this.toggleAllSelected();
    }, 1000);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue) {
      if (isObservable(this.data)) {
        (this.data as Observable<T[]>).subscribe((data: T[]) => {
          this.setData(data);
        });
      } else {
        this.setData(this.data as T[]);
      }
      this.setDisplayColumns();
    }

    if (changes.loading?.currentValue) {
      this.datatableMessages.emptyMessage = datatableMessages.loadingMessage;
    } else {
      this.datatableMessages.emptyMessage = datatableMessages.emptyMessage;
    }
  }

  private setTableId() {
    this.tableId = location.pathname + "/";

    this.columns.forEach((column) => {
      this.tableId += column.prop[0];
    });
  }

  //DATA CONTROLS
  public setData(data: T[]): void {
    if (data) {
      this.originalData = data;
      this.filteredData = this.originalData;
      this.setMaxPage();
      this.setPageCountArray();
      this.setDisplayData();
    }
  }

  public searchDatatable(): void {
    this.filterTable();

    this.setMaxPage();
    this.setPageCountArray();
    this.setPage(1);
  }

  private filterTable() {
    const searchColsLength = this.searchCols.length;
    const keys = this.searchCols;
    const lowercaseFilterValue = this.filterValue ? this.filterValue.toLowerCase() : "";

    this.filteredData = this.originalData.filter((item) => {
      for (let i = 0; i < searchColsLength; i++) {
        const colValue = this.getColValueFromProperty(keys, i, item);

        if (!lowercaseFilterValue || (!!colValue && colValue.toString().toLowerCase().indexOf(lowercaseFilterValue) !== -1)) {
          return true;
        }
      }
    });

    this.setDisplayData();
  }

  private getColValueFromProperty(keys: Array<string>, i: number, item: T): unknown {
    let colValue: unknown;

    if (!keys[i].includes(this.OBJECT_DELIMITER)) {
      colValue = (item as Record<string, unknown>)[keys[i]];
      return colValue;
    }

    const inner: Array<string> = keys[i].split(this.OBJECT_DELIMITER);

    inner.forEach((property, index) => {
      if (index === 0) {
        colValue = (item as Record<string, unknown>)[property];
      } else if (colValue && typeof colValue === "object") {
        colValue = (colValue as Record<string, unknown>)[property];
      }
    });

    return colValue;
  }

  //COLUMN CONTROLS
  public toggleAllSelected(): void {
    this.columns.forEach((column) => {
      column.selected = !this.allSelected;
    });
    this.setDisplayColumns();
  }

  public setDisplayColumns(): void {
    this.displayColumns = this.columns.filter((column) => {
      return column.selected;
    });
    this.allSelected =
      this.columns.filter((column) => {
        return !column.selected;
      }).length === 0;
    this.saveTableCache();
  }

  private setColumnsFromModal(columns) {
    this.columns.forEach((column) => {
      column.selected = columns.find((modalColumn) => {
        return modalColumn.prop === column.prop;
      }).selected;
    });
    this.setDisplayColumns();
  }

  private setColumnWidth() {
    if (!this.columnMode) {
      this.columnMode = "force";

      this.columns.forEach((column) => {
        if (column.hasOwnProperty("width")) this.columnMode = "fixed";
      });
    }
  }

  private loadTableCache() {
    const cachedColumns = this.cacheService.load(this.tableId);

    if (cachedColumns)
      this.columns.forEach((column) => {
        const cachedColumn = cachedColumns.find((_cachedColumn) => {
          return _cachedColumn.prop === column.prop;
        });
        if (cachedColumn) column = Object.assign(column, cachedColumn);
      });
  }

  private saveTableCache() {
    const columnCache = [];

    this.columns.forEach((column) => {
      columnCache.push({
        name: column.name,
        prop: column.prop,
        selected: column.selected,
      });
    });

    this.cacheService.save({
      key: this.tableId,
      data: columnCache,
    });
  }

  //SORTING
  public customSort(event: SortEvent): void {
    const dateFormat = "dd/MM/YYYY";
    this.filteredData.sort((a, b) => {
      const sortDirection = event.sorts[0].dir;
      const firstValue = a[event.sorts[0].prop] ?? "";
      const secondValue = b[event.sorts[0].prop] ?? "";
      const isValidDate = isValid(parse(firstValue as string, dateFormat, new Date()));

      if (isValidDate) {
        const firstDate = parse(firstValue, dateFormat, new Date());
        const secondDate = parse(secondValue, dateFormat, new Date());
        const daysDifference = differenceInDays(firstDate, secondDate);
        return sortDirection === "asc" ? daysDifference : daysDifference * -1;
      } else {
        if (sortDirection === "asc") {
          return firstValue > secondValue ? 1 : -1;
        } else {
          return firstValue > secondValue ? -1 : 1;
        }
      }
    });
    this.setDisplayData();
  }

  //EXCEL CONTROLS
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public exportExcel(): void {
    const exportData = [];
    let tempData = {};
    let dataValue;

    this.filteredData.forEach((data) => {
      tempData = {};
      this.displayColumns.forEach((column) => {
        if (column.name && column.selected && column.prop && !column.dontPrint) {
          dataValue = this.objectPropertiesService.getValue(data, column.prop);

          const dateESPFormat = parse(dataValue, "dd/MM/yyyy", new Date());
          const valueParsedDate = isValid(dateESPFormat) ? dateESPFormat : parseISO(dataValue);

          if (typeof dataValue === "boolean") {
            tempData[column.name] = dataValue ? "Sí" : "No";
          } else if (typeof dataValue === "string" && Number(dataValue)) {
            tempData[column.name] = dataValue;
          } else if (isValid(valueParsedDate)) {
            tempData[column.name] = format(valueParsedDate, "dd/MM/yyyy");
          } else if (
            typeof data[column.prop] === "number" &&
            column.prop !== "CantidadProcedimientos" &&
            column.prop !== "Numero"
          ) {
            tempData[column.name] = NumberPipe.transform(dataValue);
          } else tempData[column.name] = dataValue;
        }
      });

      exportData.push(tempData);
    });

    if (this.customExport) {
      this.excelExported.emit(exportData);
    } else {
      this.excelService.exportAsExcelFile(exportData, this.excelName || "");
    }
  }

  //ROW EVENTS
  public activate(event: RowClickEvent<T>): void {
    if (this.clickableRow && event.type === "click") {
      this.onRowClick(event.row);
    }
  }

  private onRowClick(row) {
    this.rowClicked.emit(row);
  }

  public onSelect(row: T): void {
    this.selectRow.emit([row]);
  }

  //RESPONSIVE MODAL CONTROLS
  public showFilterModal(): void {
    this.filterTableModal = this.bsModalService.show(FilterTableModal, {
      initialState: {
        filterValue: this.filterValue,
        allSelected: this.allSelected,
        columns: this.columns,
      },
    });
    this.filterTableModal.content.onClose.subscribe((nuevoFiltro) => {
      this.filterValue = nuevoFiltro.filterValue;

      this.filterTable();
      this.setColumnsFromModal(nuevoFiltro.columns);
    });
  }

  //PAGE CONTROLS
  public setMaxPage(): void {
    this.maxPage = Math.ceil(this.filteredData.length / this.pageSize);
  }
  public setPageCountArray(): void {
    if (this.pageCountArray.length > this.maxPage)
      for (let i = this.pageCountArray.length; i > this.maxPage; i--) this.pageCountArray.pop();
    else for (let i = this.pageCountArray.length; i < this.maxPage; i++) this.pageCountArray.push({ value: i + 1 });
  }

  public setPageSize(newPageSize: string): void {
    this.pageSize = parseInt(newPageSize, 10);
    this.setMaxPage();

    this.setPageCountArray();

    if (this.currentPage > this.maxPage) this.currentPage = this.maxPage;
    this.table.limit = this.pageSize;

    this.setDisplayData();
    this.setPage(1);
    this.pageSizeChange.emit(this.pageSize);
  }
  public setPage(newPage: string | number): void {
    this.currentPage = parseInt(newPage as string, 10);
    this.offset = this.pageSize * (this.currentPage - 1);
    this.setDisplayData();
    this.currentPageChange.emit(this.currentPage);
  }

  private setDisplayData() {
    this.itemCount = this.filteredData.length;
    this.displayData = this.filteredData.slice(this.offset, this.offset + this.pageSize);
    this.displayData = this.displayData.map((el) => el);
  }

  public singleSelectCheck(row: T): boolean {
    return this.selectedData.indexOf(row) === -1;
  }
}
